// frontend/src/pages/AgentDetail.tsx
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

interface Agent {
  id: string;
  name: string;
  slug: string;
  description: string;
  model: string;
  system_prompt: string;
  temperature: number;
  max_tokens: number;
  capabilities: string[];
  tools: string[];
  mcp_servers: string[];
  icon_emoji: string;
  is_default: boolean;
  is_active: boolean;
  createdAt: string;
}

interface Tool {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  provider: string;
  is_active: boolean;
}

interface MCPServer {
  id: string;
  name: string;
  slug: string;
  description: string;
  url?: string;
  transport: string;
  capabilities: string[];
  is_active: boolean;
}

interface AgentTool {
  id: string;
  agent_id: string;
  tool_id: string;
  is_enabled: boolean;
  display_order: number;
  tool_name?: string;
  tool_slug?: string;
  tool_category?: string;
}

interface AgentMCPServer {
  id: string;
  agent_id: string;
  mcp_server_id: string;
  is_enabled: boolean;
  display_order: number;
  mcp_name?: string;
  mcp_slug?: string;
  mcp_capabilities?: string[];
}

const AgentDetailContent = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const { user, isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Tools
  const [availableTools, setAvailableTools] = useState<Tool[]>([]);
  const [agentTools, setAgentTools] = useState<AgentTool[]>([]);
  const [showAddToolModal, setShowAddToolModal] = useState(false);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  
  // MCP Servers
  const [availableMCPServers, setAvailableMCPServers] = useState<MCPServer[]>([]);
  const [agentMCPServers, setAgentMCPServers] = useState<AgentMCPServer[]>([]);
  const [showAddMCPModal, setShowAddMCPModal] = useState(false);
  const [selectedMCPServers, setSelectedMCPServers] = useState<string[]>([]);

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, isReady, navigate, agentId]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        fetchAgent(),
        fetchAvailableTools(),
        fetchAgentTools(),
        fetchAvailableMCPServers(),
        fetchAgentMCPServers()
      ]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgent = async () => {
    const response = await fetch(
      apiClient.buildEndpoint(`/api/agents/${agentId}`),
      { method: 'GET' }
    );
    if (!response.ok) throw new Error('Erreur lors du chargement de l\'agent');
    const data = await response.json();
    setAgent(data);
  };

  const fetchAvailableTools = async () => {
    const response = await fetch(
      apiClient.buildEndpoint('/api/tools/'),
      { method: 'GET' }
    );
    if (!response.ok) throw new Error('Erreur lors du chargement des tools');
    const data = await response.json();
    setAvailableTools(data.tools || []);
  };

  const fetchAgentTools = async () => {
    const response = await fetch(
      apiClient.buildEndpoint(`/api/tools/agent-tools/agent/${agentId}/details`),
      { method: 'GET' }
    );
    if (!response.ok) throw new Error('Erreur lors du chargement des tools de l\'agent');
    const data = await response.json();
    console.log('Agent tools loaded:', data); // Debug
    setAgentTools(data || []);
  };

  const fetchAvailableMCPServers = async () => {
    const response = await fetch(
      apiClient.buildEndpoint('/api/mcp-servers/'),
      { method: 'GET' }
    );
    if (!response.ok) throw new Error('Erreur lors du chargement des MCP servers');
    const data = await response.json();
    setAvailableMCPServers(Array.isArray(data) ? data : []);
  };

  const fetchAgentMCPServers = async () => {
    const response = await fetch(
      apiClient.buildEndpoint(`/api/mcp-servers/agent-mcp-servers/agent/${agentId}/details`),
      { method: 'GET' }
    );
    if (!response.ok) throw new Error('Erreur lors du chargement des MCP servers de l\'agent');
    const data = await response.json();
    setAgentMCPServers(data || []);
  };

  const handleAddTools = async () => {
    if (selectedTools.length === 0) return;
    
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/tools/agent-tools/bulk?agent_id=${agentId}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(selectedTools)
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de l\'ajout des tools');
      }

      setSuccess('✓ Tools ajoutés avec succès !');
      setShowAddToolModal(false);
      setSelectedTools([]);
      fetchAgentTools();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemoveTool = async (agentToolId: string) => {
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/tools/agent-tools/${agentToolId}`),
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du tool');
      }

      setSuccess('✓ Tool supprimé avec succès !');
      fetchAgentTools();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleTool = async (agentTool: AgentTool) => {
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/tools/agent-tools/${agentTool.id}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_enabled: !agentTool.is_enabled })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la modification du tool');
      }

      fetchAgentTools();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddMCPServers = async () => {
    if (selectedMCPServers.length === 0) return;
    
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/mcp-servers/agent-mcp-servers/bulk?agent_id=${agentId}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(selectedMCPServers)
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de l\'ajout des MCP servers');
      }

      setSuccess('✓ MCP Servers ajoutés avec succès !');
      setShowAddMCPModal(false);
      setSelectedMCPServers([]);
      fetchAgentMCPServers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemoveMCPServer = async (agentMCPServerId: string) => {
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/mcp-servers/agent-mcp-servers/${agentMCPServerId}`),
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du MCP server');
      }

      setSuccess('✓ MCP Server supprimé avec succès !');
      fetchAgentMCPServers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleMCPServer = async (agentMCPServer: AgentMCPServer) => {
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/mcp-servers/agent-mcp-servers/${agentMCPServer.id}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_enabled: !agentMCPServer.is_enabled })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la modification du MCP server');
      }

      fetchAgentMCPServers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (!isReady || loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="!size-6" />
      </div>
    );
  }

  if (!isAuthenticated || !user || !agent) {
    return null;
  }

  // Filtrer les tools déjà ajoutés
  const availableToolsToAdd = availableTools.filter(
    tool => !agentTools.some(at => at.tool_id === tool.id)
  );

  // Filtrer les MCP servers déjà ajoutés
  const availableMCPServersToAdd = availableMCPServers.filter(
    mcp => !agentMCPServers.some(ams => ams.mcp_server_id === mcp.id)
  );

  return (
    <div className="flex flex-col h-full w-full overflow-auto">
      <div className="max-w-7xl mx-auto w-full p-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/agents')}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour aux agents
          </button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="text-5xl">{agent.icon_emoji}</div>
            <div>
              <h1 className="text-3xl font-bold mb-1">{agent.name}</h1>
              <p className="text-muted-foreground">{agent.description}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${
              agent.is_active
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
            }`}>
              {agent.is_active ? 'Actif' : 'Inactif'}
            </span>
            {agent.is_default && (
              <span className="inline-flex items-center px-3 py-1 rounded text-sm font-medium bg-primary/10 text-primary">
                Par défaut
              </span>
            )}
          </div>
        </div>

        {/* Messages */}
        {success && (
          <div className="mb-6 rounded-md border border-green-500 bg-green-50 dark:bg-green-950/50 p-4 text-sm text-green-700 dark:text-green-300">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tools Section */}
          <div className="rounded-lg border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">🛠️ Tools</h2>
              <button
                onClick={() => setShowAddToolModal(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Ajouter
              </button>
            </div>

            {agentTools.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun tool configuré
              </p>
            ) : (
              <div className="space-y-2">
                {agentTools.map((agentTool) => (
                  <div
                    key={agentTool.id}
                    className="flex items-center justify-between p-3 rounded-md border bg-background"
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={agentTool.is_enabled}
                        onChange={() => handleToggleTool(agentTool)}
                        className="rounded"
                      />
                      <div>
                        <p className="font-medium text-sm">{agentTool.tool_name}</p>
                        <p className="text-xs text-muted-foreground">{agentTool.tool_category}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveTool(agentTool.id)}
                      className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MCP Servers Section */}
          <div className="rounded-lg border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">🖥️ MCP Servers</h2>
              <button
                onClick={() => setShowAddMCPModal(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Ajouter
              </button>
            </div>

            {agentMCPServers.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun MCP server configuré
              </p>
            ) : (
              <div className="space-y-2">
                {agentMCPServers.map((agentMCP) => (
                  <div
                    key={agentMCP.id}
                    className="flex items-center justify-between p-3 rounded-md border bg-background"
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={agentMCP.is_enabled}
                        onChange={() => handleToggleMCPServer(agentMCP)}
                        className="rounded"
                      />
                      <div>
                        <p className="font-medium text-sm">{agentMCP.mcp_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {agentMCP.mcp_capabilities?.length || 0} capabilities
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveMCPServer(agentMCP.id)}
                      className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Agent Info */}
        <div className="mt-6 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-semibold mb-4">ℹ️ Informations</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground mb-1">Modèle</p>
              <p className="font-medium">{agent.model}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1">Temperature</p>
              <p className="font-medium">{agent.temperature}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1">Max Tokens</p>
              <p className="font-medium">{agent.max_tokens}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1">Slug</p>
              <p className="font-mono font-medium">{agent.slug}</p>
            </div>
          </div>
          
          <div className="mt-4">
            <p className="text-muted-foreground mb-2">System Prompt</p>
            <div className="p-3 rounded-md bg-muted/50 text-sm whitespace-pre-wrap">
              {agent.system_prompt}
            </div>
          </div>
        </div>
      </div>

      {/* Add Tools Modal */}
      {showAddToolModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Ajouter des tools</h2>
              <button
                onClick={() => {
                  setShowAddToolModal(false);
                  setSelectedTools([]); // Réinitialiser la sélection
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {availableToolsToAdd.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Tous les tools sont déjà ajoutés
              </p>
            ) : (
              <>
                <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                  {availableToolsToAdd.map((tool) => (
                    <label
                      key={tool.id}
                      className="flex items-center gap-3 p-3 rounded-md border hover:bg-accent cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTools.includes(tool.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTools([...selectedTools, tool.id]);
                          } else {
                            setSelectedTools(selectedTools.filter(id => id !== tool.id));
                          }
                        }}
                        className="rounded"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{tool.name}</p>
                        <p className="text-sm text-muted-foreground">{tool.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">{tool.category}</p>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={handleAddTools}
                    disabled={selectedTools.length === 0}
                    className="flex-1 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    Ajouter {selectedTools.length > 0 && `(${selectedTools.length})`}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddToolModal(false);
                      setSelectedTools([]); // Réinitialiser la sélection
                    }}
                    className="px-4 py-2 rounded-md border border-input hover:bg-accent"
                  >
                    Annuler
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add MCP Servers Modal */}
      {showAddMCPModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Ajouter des MCP Servers</h2>
              <button
                onClick={() => {
                  setShowAddMCPModal(false);
                  setSelectedMCPServers([]); // Réinitialiser la sélection
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {availableMCPServersToAdd.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Tous les MCP servers sont déjà ajoutés
              </p>
            ) : (
              <>
                <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                  {availableMCPServersToAdd.map((mcp) => (
                    <label
                      key={mcp.id}
                      className="flex items-center gap-3 p-3 rounded-md border hover:bg-accent cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMCPServers.includes(mcp.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMCPServers([...selectedMCPServers, mcp.id]);
                          } else {
                            setSelectedMCPServers(selectedMCPServers.filter(id => id !== mcp.id));
                          }
                        }}
                        className="rounded"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{mcp.name}</p>
                        <p className="text-sm text-muted-foreground">{mcp.description}</p>
                        <div className="flex gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 rounded bg-muted">
                            {mcp.transport.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded bg-muted">
                            {mcp.capabilities.length} tools
                          </span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={handleAddMCPServers}
                    disabled={selectedMCPServers.length === 0}
                    className="flex-1 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    Ajouter {selectedMCPServers.length > 0 && `(${selectedMCPServers.length})`}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddMCPModal(false);
                      setSelectedMCPServers([]); // Réinitialiser la sélection
                    }}
                    className="px-4 py-2 rounded-md border border-input hover:bg-accent"
                  >
                    Annuler
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AgentDetail() {
  return (
    <Page>
      <AgentDetailContent />
    </Page>
  );
}